<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { UserGroupIcon } from '@heroicons/vue/solid';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import UserLabelService from '@/services/api/UserLabelService';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FilterItem,
    Permission,
    SortMenuItem,
    UserLabelActions,
    UserLabelFilter,
    UserLabelGetters,
    UserLabelModel,
    UserLabelServiceGetAllParams,
} from '@/types';
import {
    UserLabelListItem,
    UserLabelListSort,
    UserLabelListFilters,
} from '@/views/pages/user-label/components';

const route = useRoute();
const router = useRouter();

const filterStore = useFilterStore();
const uiStore = useUiStore();

const userLabels = ref<UserLabelModel[]>([]);
const count = ref(0);
const perPage = 10;

const search = computed({
    get: () => store.getters[UserLabelGetters.SEARCH],
    set: (value: string) => store.dispatch(UserLabelActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[UserLabelGetters.SORT],
    set: (value: SortMenuItem[]) => store.dispatch(UserLabelActions.SET_SORT, value),
});

const level = computed(() => filterStore.level);

const currentPage = computed({
    get: () => store.getters[UserLabelGetters.PAGE],
    set: (value: number) => store.dispatch(UserLabelActions.SET_PAGE, value),
});

const filter: UserLabelFilter = store.getters[UserLabelGetters.FILTER];

const activeFilters = computed<FilterItem[]>(() => store.getters[UserLabelGetters.ACTIVE_FILTERS]);

const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

watch([currentPage], () => {
    fetchUserLabels();
});

watch([level, search, filter, orderBy], () => {
    if (currentPage.value === 1) fetchUserLabels();
    else currentPage.value = 1;
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getParams = (): UserLabelServiceGetAllParams => ({
    page: currentPage.value,
    per_page: perPage,
    order_by: orderBy.value,
    is_active: filter.isActive !== null ? (filter.isActive.value as boolean) : undefined,
    search: search.value,
});

const fetchUserLabels = async () => {
    setLoading(true);
    try {
        const { data, headers } = await UserLabelService.getAll<UserLabelModel[]>({
            params: getParams(),
        });
        userLabels.value = data;
        count.value = parseInt(headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const setIsActive = async (item: UserLabelModel, isActive: boolean) => {
    setLoading(true);
    try {
        const { data } = await (
            isActive
                ? (id: number) => UserLabelService.activate(id)
                : (id: number) => UserLabelService.inactivate(id)
        )(item.id);
        toast.success(data.message);
        await fetchUserLabels();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleRemoveSortClick = (item: SortMenuItem) => {
    const index = sortMenuSelected.value.indexOf(item);
    if (index > -1) sortMenuSelected.value.splice(index, 1);
};

const handleRemoveFilterClick = (item: FilterItem) =>
    store.dispatch(UserLabelActions.CLEAR_FILTER, item);

const handleActivateClick = (item: UserLabelModel) => setIsActive(item, true);

const handleInactivateClick = (item: UserLabelModel) => setIsActive(item, false);

onMounted(() => {
    if (route.query.clearStateOnSetup) {
        search.value = '';
        sortMenuSelected.value = [];
        currentPage.value = 1;
        router.replace({ query: {} });
    }

    fetchUserLabels();
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex items-center text-xl font-semibold text-gray-800">
                    <UserGroupIcon class="mr-1 h-6 w-6 text-gray-500" aria-hidden="true" />
                    {{ $t('core.entity.UserLabels') }}
                </h1>
            </template>
            <template #actions>
                <router-link
                    v-if="$can(Permission.CREATE_USER_LABEL)"
                    :to="{ name: 'CreateUserLabel' }"
                >
                    <UiButton variant="primary" text-variant="white">
                        {{ $t('core.actions.Add') }}
                    </UiButton>
                </router-link>
            </template>
        </UiPageHeader>

        <div class="space-y-2">
            <UiPanel class="flex px-4 py-2" no-padding>
                <UiListSearch
                    v-model="search"
                    :placeholder="$t('userLabel.filters.search')"
                    class="flex-1"
                />
                <div class="flex items-center space-x-4">
                    <UserLabelListSort v-model="sortMenuSelected" />
                    <UserLabelListFilters />
                </div>
            </UiPanel>

            <UiPanel v-if="sortMenuSelected.length || activeFilters.length" class="space-x-2">
                <UiSortMenuBadge
                    v-for="selected in sortMenuSelected"
                    :key="selected.key"
                    :item="selected"
                    @remove="handleRemoveSortClick(selected)"
                />
                <UiBadge
                    v-for="item in activeFilters"
                    :key="`activeFilter${item.field}`"
                    removable
                    @remove="handleRemoveFilterClick(item)"
                >
                    {{ $t(`userLabel.filters.${item.field}.label`) }}: {{ item.label }}
                </UiBadge>
            </UiPanel>

            <div v-if="userLabels.length" class="space-y-2">
                <UserLabelListItem
                    v-for="userLabel in userLabels"
                    :key="userLabel.id"
                    :model-value="userLabel"
                    @activate="handleActivateClick"
                    @inactivate="handleInactivateClick"
                />
            </div>
            <UiPanel v-else class="flex items-center justify-center">
                {{ $t('core.messages.noRecordsFound') }}
            </UiPanel>

            <UiPagination
                v-if="userLabels.length"
                v-model="currentPage"
                :count="count"
                :perPage="perPage"
            />
        </div>
    </div>
</template>
